'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import posthog from 'posthog-js'

interface PostHogProviderProps {
  children: React.ReactNode;
  userRole?: {
    role: "admin" | "employee" | "customer";
    userId: string;
    email: string;
  } | null;
  userProfile?: {
    role: "admin" | "employee" | "customer";
    profile: Record<string, unknown> | null;
    email: string;
  } | null;
}

export function PostHogProvider({ children, userRole, userProfile }: PostHogProviderProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isAuthenticated = !!userRole

  useEffect(() => {
    // Initialize PostHog
    if (typeof window !== 'undefined') {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: false, // We'll manually capture pageviews
        capture_pageleave: true,
        // Enable location tracking
        ip: true, // Capture IP for location data
        // Enable session recording for authenticated users only
        session_recording: {
          recordCrossOriginIframes: false,
        },
        // Enable autocapture for additional user interaction data
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit'], // Capture form interactions
        },
        // Capture additional browser/device info
        capture_performance: true,
      })
    }
  }, [])

  useEffect(() => {
    // Track page views
    if (pathname) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      posthog.capture('$pageview', {
        $current_url: url,
      })
    }
  }, [pathname, searchParams])

  // Track authentication state changes and identify user
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      // Use email as the consistent identifier (more stable than database IDs)
      const profile = userProfile.profile
      const isEmployee = userProfile.role === 'employee' || userProfile.role === 'admin'
      const isCustomer = userProfile.role === 'customer'

      const identityData: Record<string, unknown> = {
        email: userProfile.email,
        role: userProfile.role,
        displayName: profile?.displayName,
      }

      if (isEmployee && profile) {
        identityData.firstName = (profile as { firstName?: string }).firstName
        identityData.lastName = (profile as { lastName?: string }).lastName
        identityData.position = (profile as { position?: string }).position
      } else if (isCustomer && profile) {
        identityData.companyName = (profile as { companyName?: string }).companyName
        identityData.contactPerson = (profile as { contactPerson?: string }).contactPerson
      }

      posthog.identify(userProfile.email, identityData)

      // Set user properties
      posthog.setPersonProperties({
        email: userProfile.email,
        role: userProfile.role,
        displayName: profile?.displayName,
        $name: profile?.displayName,
        $email: userProfile.email,
      })

      // Track authentication event with location data
      posthog.capture('user_authenticated', {
        role: userProfile.role,
        displayName: profile?.displayName,
        sign_in_timestamp: new Date().toISOString(),
        // PostHog will automatically add location data based on IP
      })
    } else if (!isAuthenticated) {
      // Reset user identification on sign out
      posthog.reset()
    }
  }, [isAuthenticated, userProfile])

  return children
}

// Utility functions for tracking events throughout the app
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.capture(eventName, properties)
  }
}

export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.identify(userId, properties)
  }
}

export const trackUserProperties = (properties: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.setPersonProperties(properties)
  }
}

// Enhanced event tracking with automatic user context
export const trackUserAction = (action: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.capture(action, {
      ...properties,
      timestamp: new Date().toISOString(),
    })
  }
}